import { Component } from '@theme/component';

/**
 * Mobile hybrid drawer drill-down (category list → sub-panel).
 *
 * @extends {Component}
 */
class MenuDrawerHybrid extends Component {
  #promoSyncAttempts = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.#bindDrawerOpen();
    this.#mountDrawerPromoCache();
    this.#syncPromos();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#drawerDetails?.removeEventListener('toggle', this.#onDrawerToggle);
  }

  /** @type {HTMLDetailsElement | null} */
  #drawerDetails = null;

  /** Reset to root view when the drawer closes. */
  reset() {
    this.#showView('root');
  }

  #bindDrawerOpen() {
    this.#drawerDetails =
      document.querySelector('#Details-menu-drawer-container') ??
      this.closest('header-drawer')?.querySelector('#Details-menu-drawer-container') ??
      null;
    this.#drawerDetails?.addEventListener('toggle', this.#onDrawerToggle);
  }

  #onDrawerToggle = () => {
    if (this.#drawerDetails?.open) {
      this.#syncPromos();
    }
  };

  #getPromoRegistry() {
    const candidates = [
      document.getElementById('header-mega-menu-mobile-promo-data'),
      document.querySelector('.header-mega-menu__block-registry'),
    ].filter((el) => el instanceof HTMLElement);

    for (const el of candidates) {
      if (el.querySelector('[data-mega-menu-promo]')) return el;
    }

    return candidates[0] ?? null;
  }

  #syncPromos() {
    const registry = this.#getPromoRegistry();
    if (!registry) {
      if (this.#promoSyncAttempts < 30) {
        this.#promoSyncAttempts += 1;
        window.setTimeout(() => this.#syncPromos(), 100);
      }
      return;
    }

    this.#promoSyncAttempts = 0;
    this.#injectBadges(registry);

    this.querySelectorAll('[data-drawer-view]').forEach((view) => {
      if (!(view instanceof HTMLElement) || view.hidden) return;
      const handle = view.dataset.drawerView;
      if (handle && handle !== 'root') {
        this.#injectPromo(handle, registry);
      }
    });
  }

  /**
   * @param {string} handle
   */
  #showView(handle) {
    this.querySelectorAll('[data-drawer-view]').forEach((view) => {
      if (!(view instanceof HTMLElement)) return;
      const isActive = view.dataset.drawerView === handle;
      view.hidden = !isActive;
      view.classList.toggle('menu-drawer-hybrid__view--active', isActive);
    });

    if (handle !== 'root') {
      const registry = this.#getPromoRegistry();
      if (registry) this.#injectPromo(handle, registry);
    }
  }

  /**
   * @param {ParentNode} registry
   */
  #injectBadges(registry) {
    registry.querySelectorAll('[data-mega-menu-promo]').forEach((promoEl) => {
      if (!(promoEl instanceof HTMLElement)) return;

      const matchKey = promoEl.dataset.menuMatchKey;
      if (!matchKey) return;

      const badgeTemplate = promoEl.querySelector('[data-mega-menu-promo-badge]');
      if (!(badgeTemplate instanceof HTMLTemplateElement)) return;

      const badgeText = badgeTemplate.content.textContent?.trim();
      const trigger = this.querySelector(`[data-drawer-open="${matchKey}"]`);

      if (badgeText && trigger instanceof HTMLElement && !trigger.querySelector('.mega-menu-hybrid__badge')) {
        const badge = document.createElement('span');
        badge.className = 'mega-menu-hybrid__badge menu-drawer-hybrid__badge';
        badge.textContent = badgeText;
        const label = trigger.querySelector('.menu-drawer-hybrid__label');
        label?.insertAdjacentElement('afterend', badge);
      }
    });
  }

  /**
   * @param {string} handle
   * @param {ParentNode} registry
   */
  #injectPromo(handle, registry) {
    const promoEl =
      registry.querySelector(`[data-mega-menu-promo][data-menu-match-key="${handle}"]`) ??
      registry.querySelector(`[data-mega-menu-promo][data-menu-match-title="${handle}"]`);

    if (!(promoEl instanceof HTMLElement)) return;

    const slot = this.querySelector(`[data-drawer-promo="${handle}"]`);
    if (!(slot instanceof HTMLElement)) return;

    const position = promoEl.dataset.mobilePromoPosition || 'below_featured';
    slot.classList.toggle('menu-drawer__hybrid-promo--promo-first', position === 'above_featured');

    let featuredEl = slot.querySelector('.mega-menu-hybrid-panel__featured');
    if (!(featuredEl instanceof HTMLElement)) {
      const featuredTemplate = promoEl.querySelector('[data-mega-menu-promo-featured]');
      if (featuredTemplate instanceof HTMLTemplateElement) {
        slot.appendChild(featuredTemplate.content.cloneNode(true));
        featuredEl = slot.querySelector('.mega-menu-hybrid-panel__featured');
      }
    }

    let cardEl = slot.querySelector('.mega-menu-promo-card');
    if (!(cardEl instanceof HTMLElement)) {
      cardEl = this.#clonePromoCard(handle, promoEl);
      if (cardEl instanceof HTMLElement) {
        slot.appendChild(cardEl);
      }
    }

    const ordered =
      position === 'above_featured' ? [cardEl, featuredEl] : [featuredEl, cardEl];

    ordered.filter((node) => node instanceof HTMLElement).forEach((node) => {
      slot.appendChild(node);
    });
  }

  #mountDrawerPromoCache() {
    const registry = this.#getPromoRegistry();
    if (!registry) return;

    this.querySelectorAll('[data-drawer-promo]').forEach((slot) => {
      if (!(slot instanceof HTMLElement)) return;

      const handle = slot.dataset.drawerPromo;
      if (handle) this.#injectPromo(handle, registry);
    });
  }

  /**
   * @param {string} handle
   * @param {HTMLElement} promoEl
   * @returns {HTMLElement | null}
   */
  #clonePromoCard(handle, promoEl) {
    const cache = document.querySelector(`[data-drawer-promo-cache="${handle}"]`);
    const cachedCard = cache?.querySelector('.mega-menu-promo-card');
    if (cachedCard instanceof HTMLElement) {
      return cachedCard.cloneNode(true);
    }

    const source =
      document.querySelector(`[data-drawer-promo-source="${handle}"]`) ??
      promoEl.querySelector('[data-mega-menu-promo-card-source]');

    const card = source?.querySelector('.mega-menu-promo-card');
    if (card instanceof HTMLElement) {
      return card.cloneNode(true);
    }

    const cardTemplate = promoEl.querySelector('template[data-mega-menu-promo-card]');
    if (cardTemplate instanceof HTMLTemplateElement) {
      const templateCard = cardTemplate.content.querySelector('.mega-menu-promo-card');
      return templateCard instanceof HTMLElement ? templateCard.cloneNode(true) : null;
    }

    return null;
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openTrigger = target.closest('[data-drawer-open]');
    if (openTrigger instanceof HTMLElement) {
      event.preventDefault();
      const handle = openTrigger.dataset.drawerOpen;
      if (handle) this.#showView(handle);
      return;
    }

    if (target.closest('[data-drawer-back]')) {
      event.preventDefault();
      this.#showView('root');
    }
  };
}

if (!customElements.get('menu-drawer-hybrid')) {
  customElements.define('menu-drawer-hybrid', MenuDrawerHybrid);
}
